import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, LayoutDashboard, Sparkles } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMySubscriptions } from "@/hooks/useSubscription";
import { useClasses } from "@/hooks/useCurriculum";
import { formatDate } from "@/lib/format";
import type { PlanId } from "@/lib/api-types";

type SuccessState = { classId?: string; plan?: PlanId };

/**
 * Post-purchase confirmation. Reached after a verified Razorpay payment.
 * Reads the just-activated class/plan from navigation state, falling back to
 * the user's active subscription (in case of a hard refresh / deep link).
 *
 * A single, tasteful flourish: a spring-pop check mark + a few subtle confetti
 * shards (framer-motion, no new dep). Respects prefers-reduced-motion.
 */
export function SubscribeSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const state = (location.state as SuccessState | null) ?? {};

  const mySubs = useMySubscriptions();
  const classesQuery = useClasses();

  const activeSub = mySubs.data?.active ?? null;
  const classId = state.classId ?? activeSub?.class ?? null;
  const plan = state.plan ?? activeSub?.plan ?? null;

  const className = useMemo(() => {
    if (!classId) return null;
    const record = classesQuery.data?.find((c) => c.class_id === classId);
    if (record) return record.class_name;
    const n = classId.replace(/^class-/, "");
    return /^\d+$/.test(n) ? `Class ${n}` : classId;
  }, [classId, classesQuery.data]);

  return (
    <Container size="sm" className="py-12 lg:py-20">
      <div className="mx-auto max-w-lg text-center">
        {/* Celebratory check mark + confetti -------------------------------- */}
        <div className="relative mx-auto h-20 w-20">
          {!reduce ? <Confetti /> : null}
          <motion.div
            initial={reduce ? false : { scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 18 }}
            className="relative flex h-20 w-20 items-center justify-center rounded-full bg-success-soft"
          >
            <Check className="h-9 w-9 text-success" strokeWidth={3} />
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
        >
          <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight">
            You're all set!
          </h1>
          <p className="mt-3 text-text-muted">
            {className ? (
              <>
                Your <span className="font-medium text-text">{className}</span> subscription
                {plan ? " (Annual Plan)" : ""} is now active.
              </>
            ) : (
              "Your subscription is now active."
            )}
          </p>
          {activeSub?.end_date ? (
            <p className="mt-1 text-sm text-text-subtle">
              Access through {formatDate(activeSub.end_date)}.
            </p>
          ) : null}
        </motion.div>

        <Card className="mt-8 p-5 sm:p-6 text-left">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="h-4 w-4 text-brand" />
            What's unlocked
          </div>
          <ul className="mt-3 space-y-2 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              Every chapter and study mode for your class
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              AI features subject to fair-use limits
            </li>
            <li className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              Progress tracking, streaks and quizzes
            </li>
          </ul>
        </Card>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button size="lg" onClick={() => navigate("/subjects")}>
            Start studying
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button size="lg" variant="secondary" onClick={() => navigate("/dashboard")}>
            <LayoutDashboard className="h-4 w-4" />
            Go to dashboard
          </Button>
        </div>
      </div>
    </Container>
  );
}

/**
 * A handful of confetti shards radiating out from the check mark. Pure
 * framer-motion -- no canvas, no dependency. Only mounts when motion is allowed.
 */
function Confetti() {
  const shards = [
    { x: -54, y: -30, c: "bg-brand", r: -40 },
    { x: 52, y: -34, c: "bg-accent", r: 30 },
    { x: -40, y: 36, c: "bg-success", r: 18 },
    { x: 46, y: 40, c: "bg-brand", r: -25 },
    { x: 0, y: -58, c: "bg-accent", r: 12 },
    { x: -64, y: 6, c: "bg-success", r: -15 },
    { x: 62, y: 4, c: "bg-brand", r: 22 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0">
      {shards.map((s, i) => (
        <motion.span
          key={i}
          aria-hidden
          className={`absolute left-1/2 top-1/2 h-2 w-1.5 rounded-sm ${s.c}`}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.5, rotate: 0 }}
          animate={{
            x: s.x,
            y: s.y,
            opacity: [0, 1, 1, 0],
            scale: 1,
            rotate: s.r,
          }}
          transition={{ duration: 1.1, delay: 0.1 + i * 0.03, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}
